import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect, useMemo } from 'react';

import { API, getRequestBlob } from '@/api/api';
import { getErrorStatus, isMediaNotReady } from '@/common/error';
import { MEDIA_POLL_INTERVAL } from '@/common/media';
import { authorizedFileQueryKey, isAuthorizedFileLink } from '@/utils/file-links';

// Files are no longer served from the storage bucket: every service hands out a
// *private link* (`…/api/v1/<service>/files/…`) that only answers to the bearer
// token and X-Service-Id header, which `<img src>` / fetch() / react-pdf cannot
// attach — they get a 403. Fetch the file through axios (both headers added by
// the request interceptors) and expose it as a blob: URL any consumer can use.
//
// The Blob is cached in react-query keyed by storageLink, so the card thumbnail
// and the full preview share a single network request. Each consumer derives
// its own object URL from that shared Blob and revokes it on unmount.
//
// A chat video does not come through here: its endpoint serves byte ranges, so
// it is streamed by the <video> element itself — see `useStreamingFileUrl`.
//
// A video or audio file the encoder is still working on answers 409 instead of
// bytes. That is `processing`, not `error`: the link is asked again every few
// seconds and resolves by itself once the playable copy is in place.
type Options = {
  /** Hold the request back until the file is actually wanted. */
  enabled?: boolean;
  /** Raise the 15-second request timeout for files too big to arrive in it. */
  longTimeout?: boolean;
};

export const useAuthorizedFileUrl = (
  storageLink: null | string | undefined,
  { enabled = true, longTimeout = false }: Options = {},
) => {
  const needsAuth = isAuthorizedFileLink(storageLink);

  const {
    data: blob,
    isError,
    error,
  } = useQuery<Blob>({
    queryKey: authorizedFileQueryKey(storageLink),
    enabled: needsAuth && enabled,
    staleTime: Infinity, // file content at a given link is immutable
    // A deleted file or one this seat may not open answers 403/404 every time —
    // retrying only multiplies the failed requests behind every thumbnail. A
    // cancelled one was cancelled on purpose and must not come back either.
    retry: (failureCount, error) => {
      if (axios.isCancel(error)) return false;
      const status = getErrorStatus(error);
      if (status && status >= 400 && status < 500) return false;
      return navigator.onLine && failureCount < 3;
    },
    refetchInterval: (query) => (isMediaNotReady(query.state.error) ? MEDIA_POLL_INTERVAL : false),
    queryFn: ({ signal }) => {
      // Private links are absolute, so axios ignores API.DOCUMENTS' baseURL and
      // asks the service that issued the link; the client only adds the headers.
      return getRequestBlob(API.DOCUMENTS, storageLink!, undefined, longTimeout, {
        signal,
      });
    },
  });

  const url = useMemo(() => {
    if (!needsAuth) return storageLink ?? null;
    return blob ? URL.createObjectURL(blob) : null;
  }, [needsAuth, storageLink, blob]);

  // Revoke only object URLs we created; leave passthrough links untouched.
  useEffect(() => {
    if (!url || !needsAuth) return;
    return () => URL.revokeObjectURL(url);
  }, [url, needsAuth]);

  const processing = isError && isMediaNotReady(error);

  return {
    url,
    error: isError && !processing,
    processing,
    isLoading: needsAuth && enabled && !blob && (!isError || processing),
  };
};
